import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import styles from './DeleteAccount.module.scss';
import { Helmet } from 'react-helmet';

const DeleteAccount = () => {
  const emailSubject = encodeURIComponent('Account Deletion Request - Yesterday Stories');
  const emailBody = encodeURIComponent(`Dear Yesterday Stories Team,

I would like to permanently delete my Yesterday Stories account.

Account Details:
- Email Address: [Enter your registered email here]
- Phone Number: [Enter your phone number here]
- Postcode: [Enter your postcode here]

Reason for deletion (optional): [Enter reason here]

I understand that:
- This action is permanent and cannot be undone
- All my data will be permanently deleted
- Processing may take up to 30 business days

Thank you for your assistance.

Best regards`);

  const mailtoLink = `mailto:info@yesterdaystories.com.au?subject=${emailSubject}&body=${emailBody}`;

  return (
    <>
      <Helmet>
        <title>Delete Your Account - Yesterday Stories</title>
        <meta name="description" content="Request permanent deletion of your Yesterday Stories account. Contact info@yesterdaystories.com.au with your email, phone number, and postcode." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      {/* Add spacing for fixed header */}
      <br/><br/><br/><br/><br/><br/><br/>
      
      <div className={styles.pageWrapper}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              <div className={styles.headerSection}>
                <h1 className={styles.mainTitle}>Account Deletion Request</h1>
                <p className={styles.subtitle}>Permanently delete your Yesterday Stories account and all associated data</p>
              </div>
              
              <div className={styles.warningSection}>
                <div className={styles.warningHeader}>
                  <span className={styles.warningIcon}>⚠️</span>
                  <h2>Important: This Action Cannot Be Undone</h2>
                </div>
                <ul className={styles.warningList}>
                  <li><strong>All your data will be permanently deleted</strong> including comments, preferences, and account information</li>
                  <li><strong>You will lose access</strong> to your Yesterday Stories account immediately after processing</li>
                  <li><strong>This process cannot be reversed</strong> once completed</li>
                  <li><strong>Processing takes up to 30 business days</strong> to ensure complete data removal</li>
                </ul>
              </div>
              
              <div className={styles.contactSection}>
                <h2>📧 How to Delete Your Account</h2>
                <p>To proceed with account deletion, send an email with your account details to our support team:</p>
                
                <div className={styles.contactCard}>
                  <div className={styles.emailAddress}>info@yesterdaystories.com.au</div>
                  <p className={styles.subjectLine}><strong>Subject:</strong> Account Deletion Request</p>
                  
                  <a href={mailtoLink} className={styles.emailButton}>
                    📧 Send Deletion Request Email
                  </a>
                </div>
              </div>
              
              <Row>
                <Col md={6}>
                  <div className={styles.requirementsSection}>
                    <h3>📋 Required Information</h3>
                    <p>For security and verification purposes, please include:</p>
                    
                    <div className={styles.requirementsList}>
                      <div className={styles.requirementItem}>
                        <span className={styles.requirementNumber}>1</span>
                        <div>
                          <strong>Email Address</strong><br/>
                          Your registered Yesterday Stories account email
                        </div>
                      </div>
                      
                      <div className={styles.requirementItem}>
                        <span className={styles.requirementNumber}>2</span>
                        <div>
                          <strong>Phone Number</strong><br/>
                          For identity verification purposes
                        </div>
                      </div>
                      
                      <div className={styles.requirementItem}>
                        <span className={styles.requirementNumber}>3</span>
                        <div>
                          <strong>Postcode</strong><br/>
                          To help verify account ownership
                        </div>
                      </div>
                      
                      <div className={styles.requirementItem}>
                        <span className={styles.requirementNumber}>✓</span>
                        <div>
                          <strong>Confirmation Statement</strong><br/>
                          Confirm you understand this action is permanent
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
                
                <Col md={6}>
                  <div className={styles.timelineSection}>
                    <h3>⏱️ Processing Timeline</h3>
                    
                    <div className={styles.timelineList}>
                      <div className={styles.timelineItem}>
                        <span className={styles.timelineNumber}>1</span>
                        <div>
                          <strong>Email Received</strong><br/>
                          Acknowledgment within 2 business days
                        </div>
                      </div>
                      
                      <div className={styles.timelineItem}>
                        <span className={styles.timelineNumber}>2</span>
                        <div>
                          <strong>Identity Verification</strong><br/>
                          Account verification (3-5 business days)
                        </div>
                      </div>
                      
                      <div className={styles.timelineItem}>
                        <span className={styles.timelineNumber}>3</span>
                        <div>
                          <strong>Account Deletion</strong><br/>
                          Complete data removal (up to 30 business days)
                        </div>
                      </div>
                      
                      <div className={styles.timelineItem}>
                        <span className={styles.timelineNumber}>4</span>
                        <div>
                          <strong>Confirmation</strong><br/>
                          Final confirmation email sent
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className={styles.templateSection}>
                <h3>📝 Email Template</h3>
                <p>Copy and customize this template for your deletion request:</p>
                
                <div className={styles.emailTemplate}>
                  <strong>To:</strong> info@yesterdaystories.com.au<br/>
                  <strong>Subject:</strong> Account Deletion Request - Yesterday Stories<br/><br/>
                  
                  Dear Yesterday Stories Team,<br/><br/>
                  
                  I would like to permanently delete my Yesterday Stories account.<br/><br/>
                  
                  <strong>Account Details:</strong><br/>
                  - Email Address: <span className={styles.placeholder}>[Your registered email]</span><br/>
                  - Phone Number: <span className={styles.placeholder}>[Your phone number]</span><br/>
                  - Postcode: <span className={styles.placeholder}>[Your postcode]</span><br/><br/>
                  
                  Reason for deletion (optional): <span className={styles.placeholder}>[Your reason]</span><br/><br/>
                  
                  I understand that:<br/>
                  - This action is permanent and cannot be undone<br/>
                  - All my data will be permanently deleted<br/>
                  - Processing may take up to 30 business days<br/><br/>
                  
                  Thank you for your assistance.<br/><br/>
                  
                  Best regards,<br/>
                  <span className={styles.placeholder}>[Your name]</span>
                </div>
              </div>
              
              <div className={styles.helpSection}>
                <h3>📞 Need Help?</h3>
                <p>If you have questions about the account deletion process, please contact us at <strong>info@yesterdaystories.com.au</strong></p>
                <p className={styles.helpNote}><em>Please note: Once we begin processing your deletion request, it cannot be stopped or reversed.</em></p>
              </div>
              
              <div className={styles.backSection}>
                <a href="/" className={styles.backLink}>← Return to Yesterday Stories Home</a>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default DeleteAccount;